import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import SEO from "@/components/SEO";
import BeforeAfter from "@/components/BeforeAfter";
import CtaFinal from "@/components/CtaFinal";
import { Reveal, MaskedLineInView, Eyebrow } from "@/components/Reveal";
import { IMG, PORTFOLIO, PORTFOLIO_CATEGORIES } from "@/data/content";

const Lavori = () => {
  const [filter, setFilter] = useState("Tutti");
  const items = filter === "Tutti" ? PORTFOLIO : PORTFOLIO.filter((p) => p.category === filter);

  return (
    <>
      <SEO
        title="I Nostri Lavori — SOPAJ | Ristrutturazioni tra Nibionno, Lecco e Brianza"
        description="Galleria fotografica dei lavori Sopaj: ristrutturazioni complete, appartamenti, ville, bagni, cucine, pavimenti, opere murarie, cartongessi ed esterni."
        path="/lavori"
      />

      <section data-testid="lavori-hero" className="max-w-7xl mx-auto px-5 md:px-8 pt-36 md:pt-48 pb-12 md:pb-16">
        <Reveal><Eyebrow number="01" label="Portfolio" /></Reveal>
        <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-anthracite">
          <MaskedLineInView>I nostri <span className="font-serif italic font-normal">lavori.</span></MaskedLineInView>
        </h1>
        <Reveal delay={0.2}>
          <p className="mt-8 max-w-2xl text-anthracite/75 leading-relaxed text-base md:text-lg">
            Una selezione dei nostri interventi, dalla zona di Nibionno, Lecco e
            Brianza ad altre località. Stiamo pubblicando progressivamente la
            documentazione fotografica completa
            dei cantieri: le immagini di questa pagina illustrano le tipologie di
            lavorazione che seguiamo ogni giorno.
          </p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-24 md:pb-32">
        <Reveal delay={0.1}>
          <div data-testid="portfolio-filters" className="flex flex-wrap gap-2 mb-12 md:mb-16">
            {["Tutti", ...PORTFOLIO_CATEGORIES].map((c) => (
              <button
                key={c}
                data-testid={`filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => setFilter(c)}
                className={`font-display text-xs tracking-[0.15em] uppercase px-4 py-2.5 border transition-colors duration-300 ${
                  filter === c
                    ? "bg-terracotta border-terracotta text-ivory"
                    : "border-anthracite/20 text-anthracite/70 hover:border-terracotta hover:text-terracotta"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </Reveal>

        <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
          <AnimatePresence mode="popLayout">
            {items.map((p) => (
              <motion.article
                layout
                key={p.id}
                data-testid={`portfolio-item-${p.id}`}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="group"
              >
                <div className="relative overflow-hidden aspect-[4/3] bg-sand">
                  <img
                    src={p.img}
                    alt={`${p.title} — Sopaj impresa edile`}
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-carbone/0 group-hover:bg-carbone/35 transition-colors duration-500 flex items-end">
                    <p className="p-5 font-display text-ivory text-sm tracking-[0.15em] uppercase opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-[opacity,transform] duration-500">
                      {p.category}
                    </p>
                  </div>
                </div>
                <h2 className="mt-5 font-display text-xl tracking-tight text-anthracite group-hover:text-terracotta transition-colors duration-300">
                  {p.title}
                </h2>
                <p className="mt-1 text-sm text-concrete">{p.category}</p>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>
      </section>

      <section data-testid="lavori-prima-dopo" className="bg-sand/60">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-24 md:py-32">
          <Reveal><Eyebrow number="02" label="Prima / Dopo" /></Reveal>
          <h2 className="mt-6 mb-12 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-anthracite">
            <MaskedLineInView>Il cambiamento <span className="font-serif italic font-normal">si vede.</span></MaskedLineInView>
          </h2>
          <Reveal delay={0.15}>
            <BeforeAfter beforeImg={IMG.prima} afterImg={IMG.dopo} />
            <p className="mt-4 text-sm text-concrete">
              Un confronto reale tra lo stato iniziale e la consegna: qui
              pubblicheremo i prossimi prima/dopo documentati nei cantieri Sopaj.
            </p>
          </Reveal>
        </div>
      </section>

      <CtaFinal />
    </>
  );
};

export default Lavori;
